<script setup lang="ts">
import { usePersonalAccountDataState } from '../../utilities/account/useAccountDataPersonal'
import { useLegalAccountDataState } from '../../utilities/account/useAccountDataLegal'
import WizardContainer from './WizardContainer.vue'
import { computed, provide, ref } from 'vue'
import NaturalLegalSelectTab from './ESAC/NaturalLegalSelectTab.vue'
import NaturalProfileTinTab from './ESAC/NaturalProfileTinTab.vue'
import NaturalProfilePersonalDataTab from './ESAC/NaturalProfilePersonalDataTab.vue'
import NaturalProfileRiskToleranceTab from './ESAC/NaturalProfileRiskToleranceTab.vue'
import NaturalProfileAddressDataTab from './ESAC/NaturalProfileAddressDataTab.vue'
import NaturalProfileInvestmentTargetsTab from './ESAC/NaturalProfileInvestmentTargetsTab.vue'
import NaturalProfileFinancialConditionsTab from './ESAC/NaturalProfileFinancialConditionsTab.vue'
import NaturalProfileExperienceAndKnowledgeTab from './ESAC/NaturalProfileExperienceAndKnowledgeTab.vue'
import NaturalProfileFatcaDeclarationTab from './ESAC/NaturalProfileFatcaDeclarationTab.vue'
import NaturalProfileAcknowledgmentsOneTab from './ESAC/NaturalProfileAcknowledgmentsOneTab.vue'
import NaturalProfileAcknowledgmentsTwoTab from './ESAC/NaturalProfileAcknowledgmentsTwoTab.vue'
import NaturalKycFormTab from './ESAC/NaturalKycFormTab.vue'
import { isEsacLegalComplianceProfileComplete, isEsacNaturalComplianceProfileComplete } from './ESAC/form/forms'
import { asyncComputed } from '@vueuse/core'
import CompanyInformationTab from './ESAC/legal/CompanyInformationTab.vue'
import LegalAddressInformationTab from './ESAC/legal/LegalAddressInformationTab.vue'
import LegalManagerOnePersonalDataTab from './ESAC/legal/LegalManagerOnePersonalDataTab.vue'
import LegalManagerOneAddressDataTab from './ESAC/legal/LegalManagerOneAddressDataTab.vue'
import LegalManagerOneTaxInformationTab from './ESAC/legal/LegalManagerOneTaxInformationTab.vue'
import LegalManagerOneExperienceAndKnowledgeTab from './ESAC/legal/LegalManagerOneExperienceAndKnowledgeTab.vue'
import LegalManagersAndBeneficialOwnersOverviewTab from './ESAC/legal/LegalManagersAndBeneficialOwnersOverviewTab.vue'
import LegalManagerAuthorizationRequiredTab from './ESAC/legal/LegalManagerAuthorizationRequiredTab.vue'
import LegalEntitiesInvestmentObjectivesTab from './ESAC/legal/LegalEntitiesInvestmentObjectivesTab.vue'
import LegalEntitiesFinancialConditionsTab from './ESAC/legal/LegalEntitiesFinancialConditionsTab.vue'
import LegalEntitiesRiskToleranceTab from './ESAC/legal/LegalEntitiesRiskToleranceTab.vue'
import LegalEntitiesFatcaDeclarationTab from './ESAC/legal/LegalEntitiesFatcaDeclarationTab.vue'
import LegalEntitiesTaxSituationTab from './ESAC/legal/LegalEntitiesTaxSituationTab.vue'
import LegalEntitiesCrsTab from './ESAC/legal/LegalEntitiesCrsTab.vue'
import LegalAcknowledgementsOneTab from './ESAC/legal/LegalAcknowledgementsOneTab.vue'
import LegalAcknowledgementsTwoTab from './ESAC/legal/LegalAcknowledgementsTwoTab.vue'
import LegalKycFormTab from './ESAC/legal/LegalKycFormTab.vue'
import LegalManagerTwoPersonalDataTab from './ESAC/legal/LegalManagerTwoPersonalDataTab.vue'
import LegalManagerTwoAddressDataTab from './ESAC/legal/LegalManagerTwoAddressDataTab.vue'
import LegalManagerTwoTaxInformationTab from './ESAC/legal/LegalManagerTwoTaxInformationTab.vue'
import LegalManagerTwoExperienceAndKnowledgeTab from './ESAC/legal/LegalManagerTwoExperienceAndKnowledgeTab.vue'
import LegalBeneficialOwnerPersonalDataTab from './ESAC/legal/LegalBeneficialOwnerPersonalDataTab.vue'

// TODO: Those load the same request in the background, but the state is not shared
const personalState = usePersonalAccountDataState()
personalState.fetchAccountData()
const legalState = useLegalAccountDataState()
legalState.fetchAccountData()

const activeTab = computed(() => {
    return getCurrentTabOrder()[activeTabKey.value]
})
const activeTabKey = ref('natural_legal_select')

const isWizardFinished = asyncComputed(async () => {
    if (!usePersonalAccountDataState().loaded || !useLegalAccountDataState().loaded) {
        return true
    }

    let isNaturalWizardCompleted = await isEsacNaturalComplianceProfileComplete()

    let isLegalWizardCompleted = await isEsacLegalComplianceProfileComplete()

    return (
        isLegalWizardCompleted ||
        isNaturalWizardCompleted ||
        usePersonalAccountDataState().naturalProfile?.kyc_id === true ||
        useLegalAccountDataState().legalProfile?.kyc_legal === true
    )
}, true)

const naturalTabOrder = {
    natural_legal_select: NaturalLegalSelectTab,
    natural_personal_data: NaturalProfilePersonalDataTab,
    natural_address_data: NaturalProfileAddressDataTab,
    natural_investment_targets: NaturalProfileInvestmentTargetsTab,
    natural_financial_conditions: NaturalProfileFinancialConditionsTab,
    naturaL_experience: NaturalProfileExperienceAndKnowledgeTab,
    natural_risk_tolerance: NaturalProfileRiskToleranceTab,
    natural_fatca: NaturalProfileFatcaDeclarationTab,
    natural_tin: NaturalProfileTinTab,
    natural_acknowledgements_one: NaturalProfileAcknowledgmentsOneTab,
    natural_acknowledgements_two: NaturalProfileAcknowledgmentsTwoTab,
    natural_kyc_form: NaturalKycFormTab,
}

const legalTabOrder = {
    natural_legal_select: NaturalLegalSelectTab,
    legal_company_information: CompanyInformationTab,
    legal_address_information: LegalAddressInformationTab,
    legal_manager_one_personal: LegalManagerOnePersonalDataTab,
    legal_manager_one_address: LegalManagerOneAddressDataTab,
    legal_manager_one_tax_information: LegalManagerOneTaxInformationTab,
    legal_manager_one_experience_and_knowledge: LegalManagerOneExperienceAndKnowledgeTab,
    legal_add_more_managers_and_beneficial_owners: LegalManagersAndBeneficialOwnersOverviewTab,
    legal_entities_investment_objectives: LegalEntitiesInvestmentObjectivesTab,
    legal_entities_financial_conditions: LegalEntitiesFinancialConditionsTab,
    legal_entities_risk_tolerance: LegalEntitiesRiskToleranceTab,
    legal_entities_tax_situation: LegalEntitiesTaxSituationTab,
    legal_entities_fatca_declaration: LegalEntitiesFatcaDeclarationTab,
    legal_entities_crs: LegalEntitiesCrsTab,
    legal_entities_acknowledgements_one: LegalAcknowledgementsOneTab,
    legal_entities_acknowledgements_two: LegalAcknowledgementsTwoTab,
    legal_kyc_form: LegalKycFormTab,
}

const legalAdditionalManagerOrder = {
    legal_additional_manager_personal: LegalManagerTwoPersonalDataTab,
    legal_additional_manager_address: LegalManagerTwoAddressDataTab,
    legal_additional_manager_tax_information: LegalManagerTwoTaxInformationTab,
    legal_additional_manager_authorization_required: LegalManagerAuthorizationRequiredTab,
    legal_additional_manager_experience_and_knowledge: LegalManagerTwoExperienceAndKnowledgeTab,
}

const legalAdditionalBeneficialOwnerOrder = {
    legal_additional_ubo_personal: LegalBeneficialOwnerPersonalDataTab,
}

function navigateToTab(componentKey: string) {
    activeTabKey.value = componentKey
}

function getCurrentTabOrder() {
    if (activeTabKey.value in naturalTabOrder) {
        return naturalTabOrder
    } else if (activeTabKey.value in legalAdditionalManagerOrder) {
        return legalAdditionalManagerOrder
    } else if (activeTabKey.value in legalAdditionalBeneficialOwnerOrder) {
        return legalAdditionalBeneficialOwnerOrder
    } else {
        return legalTabOrder
    }
}

function navigateToNextTab() {
    const currentTabIndex = Object.keys(getCurrentTabOrder()).indexOf(activeTabKey.value)
    if (currentTabIndex !== -1 && currentTabIndex < Object.keys(getCurrentTabOrder()).length - 1) {
        activeTabKey.value = Object.keys(getCurrentTabOrder())[currentTabIndex + 1]
    }

    // handle create beneficial owners and managers process
    if (currentTabIndex === Object.keys(getCurrentTabOrder()).length - 1) {
        if (
            activeTabKey.value in legalAdditionalManagerOrder ||
            activeTabKey.value in legalAdditionalBeneficialOwnerOrder
        ) {
            activeTabKey.value = 'legal_add_more_managers_and_beneficial_owners'
        }
    }
}

function navigateToPreviousTab() {
    const currentTabIndex = Object.keys(getCurrentTabOrder()).indexOf(activeTabKey.value)

    if (
        currentTabIndex === 0 &&
        (getCurrentTabOrder() === legalAdditionalManagerOrder ||
            getCurrentTabOrder() === legalAdditionalBeneficialOwnerOrder)
    ) {
        activeTabKey.value = 'legal_add_more_managers_and_beneficial_owners'
    }

    if (currentTabIndex !== -1 && currentTabIndex > 0) {
        activeTabKey.value = Object.keys(getCurrentTabOrder())[currentTabIndex - 1]
    }
}

function getMaxStepCount() {
    if (getCurrentTabOrder() === naturalTabOrder || getCurrentTabOrder() === legalTabOrder) {
        return Object.keys(getCurrentTabOrder()).length - 1 // -1 because the first tab is not a step
    }
    return Object.keys(getCurrentTabOrder()).length // -1 because the first tab is not a step
}

function getCurrentStep() {
    if (getCurrentTabOrder() === naturalTabOrder || getCurrentTabOrder() === legalTabOrder) {
        return Object.keys(getCurrentTabOrder()).indexOf(activeTabKey.value)
    }
    return Object.keys(getCurrentTabOrder()).indexOf(activeTabKey.value) + 1
}

provide('navigation', {
    navigateToTab,
    navigateToNextTab,
    navigateToPreviousTab,
    getMaxStepCount,
    getCurrentStep,
})
</script>

<template>
    <WizardContainer v-if="!isWizardFinished">
        <component :is="activeTab" />
    </WizardContainer>
</template>

<style scoped></style>
